import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { IProperty, IPropertyInitialState } from "./propertyModel";
import {
  ActiveInactiveProperty,
  PropertyDetailsImageUpload,
  createProperty,
  deleteGalleryImage,
  deleteProperty,
  deletePropertyImage,
  getAllActiveProperties,
  getAllProperties,
  getPropertyByID,
  updateProperty,
} from "./services/property.services";

const initialState: IPropertyInitialState = {
  loading: false,
  formLoading: false,
  propertyImageLoading: false,
  galleryImageLoading: false,
  error: "",
  PropertyID: null,
  PropertyImageID: null,
  GalleryImageID: null,
  PropertyList: [],
  PropertyDetail: {
    ID: null,
    PropertyName: "",
    PropertyURL: "",
    BookingURL: "",
    TagLine: "",
    Address: "",
    MapURL: "",
    MobileNo: "",
    EmailID: "",
    ShortDescription: "",
    LongDescription: "",
    DiningInfo: "",
    Tags: "",
    SequenceNo: null,
    TotalRoom: null,
    IsHightlightProperty: false,
    MetaTitle: "",
    MetaDescription: "",
    MetaKeywords: "",
    IsActive: 1,
  },
};

const propertySlice = createSlice({
  name: "property",
  initialState,
  reducers: {
    clearPropertyDetails: (state) => {
      state.PropertyDetail = initialState.PropertyDetail;
    },
    setPropertyID: (state, action) => {
      state.PropertyID = action.payload;
    },
    clearPropertyID: (state) => {
      state.PropertyID = initialState.PropertyID;
    },
    setPropertyImageID: (state, action) => {
      state.PropertyImageID = action.payload;
    },
    clearPropertyImageID: (state) => {
      state.PropertyImageID = initialState.PropertyImageID;
    },
    setGalleryImageID: (state, action) => {
      state.GalleryImageID = action.payload;
    },
    clearGalleryImageID: (state) => {
      state.GalleryImageID = initialState.GalleryImageID;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getAllProperties.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(
      getAllProperties.fulfilled,
      (state, action: PayloadAction<IProperty[]>) => {
        state.loading = false;
        state.PropertyList = action.payload || [];
      },
    );
    builder.addCase(getAllProperties.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
      state.PropertyList = [];
    });
    builder.addCase(createProperty.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(createProperty.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(createProperty.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
    });
    builder.addCase(updateProperty.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(updateProperty.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(updateProperty.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
    });
    builder.addCase(deleteProperty.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(deleteProperty.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(deleteProperty.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
    });
    builder.addCase(getPropertyByID.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(getPropertyByID.fulfilled, (state, action: any) => {
      state.loading = false;
      state.PropertyDetail = action.payload;
    });
    builder.addCase(getPropertyByID.rejected, (state, action) => {
      state.loading = false;
      state.PropertyDetail = initialState.PropertyDetail;
      state.error = action.error.message || "Something went wrong";
    });
    builder.addCase(getAllActiveProperties.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(
      getAllActiveProperties.fulfilled,
      (state, action: PayloadAction<IProperty[]>) => {
        state.loading = false;
        state.PropertyList = action.payload || [];
      },
    );
    builder.addCase(getAllActiveProperties.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
      state.PropertyList = [];
    });
    builder.addCase(ActiveInactiveProperty.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(ActiveInactiveProperty.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(ActiveInactiveProperty.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
    });

    // IMAGE UPLOAD
    builder.addCase(PropertyDetailsImageUpload.pending, (state) => {
      state.loading = true;
      state.error = "";
    });
    builder.addCase(PropertyDetailsImageUpload.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(PropertyDetailsImageUpload.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message || "Something went wrong";
    });

    builder.addCase(deletePropertyImage.pending, (state) => {
      state.propertyImageLoading = true;
      state.error = "";
    });
    builder.addCase(deletePropertyImage.fulfilled, (state) => {
      state.propertyImageLoading = false;
    });
    builder.addCase(deletePropertyImage.rejected, (state, action) => {
      state.propertyImageLoading = false;
      state.error = action.error.message || "Something went wrong";
    });

    builder.addCase(deleteGalleryImage.pending, (state) => {
      state.galleryImageLoading = true;
      state.error = "";
    });
    builder.addCase(deleteGalleryImage.fulfilled, (state) => {
      state.galleryImageLoading = false;
    });
    builder.addCase(deleteGalleryImage.rejected, (state, action) => {
      state.galleryImageLoading = false;
      state.error = action.error.message || "Something went wrong";
    });
  },
});

export const {
  clearPropertyDetails,
  clearPropertyID,
  setPropertyID,
  setPropertyImageID,
  clearPropertyImageID,
  setGalleryImageID,
  clearGalleryImageID,
} = propertySlice.actions;
export default propertySlice.reducer;
